'use client';
import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import type { StorefrontCategory } from '@/api/menu-list/storefront-menu-snapshot.types';
import { useStorefrontItemsIndex } from '@/entities/product/lib/useStorefrontMenu';
import { QueryApi } from '@/features/query/api';
import { CategoryItemsConfigContext } from '@/shared/ui/providers/category-items-config-provider';
import { CategoryContext } from '@/shared/ui/providers/category-provider';
import { MenuItemData, MenuItemProvider } from '@/shared/ui/providers/menu-item-provider';
import { normalizeId, resolveInjections, resolveOverride } from './helpers';
import { CategoryItemsData } from './types';

type Props = Omit<CategoryItemsData, 'data'>;

const resolveMenuListId = (
    category: StorefrontCategory,
    menuLists?: { id?: string; categories?: StorefrontCategory[] }[],
) => {
    if (!menuLists) return null;
    for (const menuList of menuLists) {
        if (!menuList.categories) continue;
        const stack = [...menuList.categories];
        while (stack.length > 0) {
            const current = stack.pop();
            if (!current) continue;
            if (current.id === category.id) {
                return menuList.id ?? null;
            }
            if (current.children?.length) {
                stack.push(...current.children);
            }
        }
    }
    return null;
};

const getCategoryItems = (
    category: StorefrontCategory,
    menuListId: string | null,
    itemsLookup: ReturnType<typeof useStorefrontItemsIndex>,
): MenuItemData[] =>
    category.itemHashes.flatMap((hash, sortIndex) => {
        const entry = itemsLookup[hash];
        if (!entry?.placement.slug) return [];
        return [
            {
                hash,
                slug: entry.placement.slug,
                itemId: entry.body.id,
                item: entry.body,
                menuListId,
                categoryId: category.id,
                sortIndex,
                overrideItem: entry.placement.overridePrices?.length
                    ? { prices: entry.placement.overridePrices }
                    : null,
            },
        ];
    });

export const CategoryItemsFor = ({ children, limit, injections, overrides }: Props) => {
    const { data: category } = useContext(CategoryContext);
    const itemsConfig = useContext(CategoryItemsConfigContext);
    const menuListsQuery = useQuery(QueryApi.MenuList.queries.MenuLists);
    const itemsLookup = useStorefrontItemsIndex();

    const mergedOverrides = useMemo(
        () => [...(overrides ?? []), ...(itemsConfig.overrides ?? [])],
        [overrides, itemsConfig.overrides],
    );
    const mergedInjections = useMemo(
        () => [...(injections ?? []), ...(itemsConfig.injections ?? [])],
        [injections, itemsConfig.injections],
    );
    const mergedLimit = limit !== undefined ? limit : itemsConfig.limit;

    const menuListId = resolveMenuListId(category, menuListsQuery.data?.menuLists);
    const items = getCategoryItems(category, menuListId, itemsLookup);
    const limitedItems = typeof mergedLimit === 'number' ? items.slice(0, Math.max(0, mergedLimit)) : items;

    return (
        <>
            {limitedItems.flatMap((item, index) => {
                const itemId = normalizeId(item.itemId);
                const hash = normalizeId(item.hash);
                const override = resolveOverride(mergedOverrides, itemId, hash, index);
                const itemChildren = override?.children ?? children;
                const before = resolveInjections(mergedInjections, itemId, hash, index, 'before');
                const after = resolveInjections(mergedInjections, itemId, hash, index, 'after');

                return [
                    ...before,
                    <MenuItemProvider
                        key={item.hash ?? itemId ?? `${index}`}
                        value={{
                            data: item,
                            index,
                            itemsIndex: itemsLookup,
                        }}
                    >
                        {itemChildren}
                    </MenuItemProvider>,
                    ...after,
                ];
            })}
        </>
    );
};
